import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap, useIsPhone } from '@/lib/hooks';
import { Button } from './Primitives';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** The sticky action row in the header. */
  actions?: ReactNode;
  children: ReactNode;
  /** Id used for aria-labelledby. */
  id?: string;
}

/**
 * 560px from the right on desktop, pushing nothing so the list behind stays visible and
 * keyboard navigable. On a phone it is a bottom sheet with a drag handle snapping at 60 and
 * 100 percent. Escape closes it, focus is trapped while it is open and restored after.
 */
export function Drawer({ open, onClose, title, actions, children, id = 'drawer' }: DrawerProps) {
  const isPhone = useIsPhone();
  const [snap, setSnap] = useState<'partial' | 'full'>('partial');
  const trapRef = useFocusTrap(open);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (open) setSnap('partial');
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <button
        type="button"
        className="o-scrim"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
      />
      <aside
        className="o-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        data-snap={snap}
        ref={(node) => {
          trapRef.current = node;
        }}
      >
        {isPhone ? (
          <button
            type="button"
            className="o-drawer__handle"
            aria-label={snap === 'full' ? 'Collapse sheet' : 'Expand sheet'}
            onClick={() => setSnap((current) => (current === 'full' ? 'partial' : 'full'))}
          />
        ) : null}
        <header className="o-drawer__head">
          <h2 className="o-drawer__title" id={`${id}-title`}>
            {title}
          </h2>
          {actions}
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            Close
          </Button>
        </header>
        <div className="o-drawer__body">{children}</div>
      </aside>
    </>,
    document.body,
  );
}
